/**
 * The flag-driven booking model (integration guide §9) — five booleans on
 * the location (`BookingFlowFlags`) are the source of truth for which flow a
 * location runs; the old numeric `rule` (1/2/3) is only kept for
 * back-compat. Pure helpers — no React, no network — shared by the builder
 * (store, validation, inspector, block library) and the live `/book/:slug`
 * runtime so both derive the flow the same way.
 *
 * Exactly one of `serviceOnly` / `professionalFirst` /
 * `profAndSerNoAssociation` is on; `areProfessionalsEnabled` tracks the
 * latter two; `isCategoryEnabled` is independent. There is no auto-assign.
 */

/** @typedef {'serviceOnly'|'professionalFirst'|'serviceAndProfessional'} FlowMode */

export const FLOW_MODES = {
  SERVICE_ONLY: 'serviceOnly',
  PROFESSIONAL_FIRST: 'professionalFirst',
  SERVICE_AND_PROFESSIONAL: 'serviceAndProfessional'
};

/** @type {import('./types').BookingFlowFlags} */
export const DEFAULT_FLAGS = {
  serviceOnly: true,
  isCategoryEnabled: false,
  areProfessionalsEnabled: false,
  professionalFirst: false,
  profAndSerNoAssociation: false
};

/**
 * The flags for a mode (keeping the independent category flag).
 * @param {FlowMode} mode
 * @param {boolean} [isCategoryEnabled]
 * @returns {import('./types').BookingFlowFlags}
 */
export function flagsForMode(mode, isCategoryEnabled = false) {
  return {
    serviceOnly: mode === FLOW_MODES.SERVICE_ONLY,
    isCategoryEnabled: Boolean(isCategoryEnabled),
    areProfessionalsEnabled: mode !== FLOW_MODES.SERVICE_ONLY,
    professionalFirst: mode === FLOW_MODES.PROFESSIONAL_FIRST,
    profAndSerNoAssociation: mode === FLOW_MODES.SERVICE_AND_PROFESSIONAL
  };
}

/**
 * @param {import('./types').BookingFlowFlags} flags
 * @returns {FlowMode}
 */
export function getFlowMode(flags) {
  if (flags?.areProfessionalsEnabled && flags.professionalFirst) {
    return FLOW_MODES.PROFESSIONAL_FIRST;
  }
  if (flags?.areProfessionalsEnabled && flags.profAndSerNoAssociation) {
    return FLOW_MODES.SERVICE_AND_PROFESSIONAL;
  }
  return FLOW_MODES.SERVICE_ONLY;
}

/**
 * Flags from whatever the backend sent: `flags` when present, otherwise
 * derived from a legacy `rule` (2 = patient picks a professional for the
 * service; 1 and 3 have no professional step).
 * @param {Partial<import('./types').BookingFlowFlags>|null|undefined} flags
 * @param {number} [rule]
 * @returns {import('./types').BookingFlowFlags}
 */
export function normalizeFlags(flags, rule) {
  if (flags && typeof flags === 'object') {
    return flagsForMode(getFlowMode(flags), flags.isCategoryEnabled);
  }
  return flagsForMode(rule === 2 ? FLOW_MODES.SERVICE_AND_PROFESSIONAL : FLOW_MODES.SERVICE_ONLY);
}

/** The flags a flow config carries in `theme.bookingFlags` (legacy: `theme.bookingSetting`). */
export function getConfigFlags(config) {
  return normalizeFlags(config?.theme?.bookingFlags, config?.theme?.bookingSetting);
}

export const getConfigFlowMode = (config) => getFlowMode(getConfigFlags(config));

/**
 * The legacy `rule` still sent alongside `flags` on save (`SaveFlowDto.rule`
 * is required): a professional step maps to 2, service only to 1.
 * @param {import('./types').BookingFlowFlags} flags
 * @returns {1|2}
 */
export function ruleForFlags(flags) {
  return getFlowMode(flags) === FLOW_MODES.SERVICE_ONLY ? 1 : 2;
}

/**
 * Which builder blocks a location's flags enable — same derivation as the
 * backend's `GET /flow/:locationId/rules`. `professionalServices` renders
 * with the same servicePicker block as `services`.
 * @param {import('./types').BookingFlowFlags} flags
 * @returns {import('./types').FlowBuilderBlocks}
 */
export function blocksForFlags(flags) {
  const mode = getFlowMode(flags);
  return {
    category: Boolean(flags?.isCategoryEnabled),
    professional: mode !== FLOW_MODES.SERVICE_ONLY,
    services: mode !== FLOW_MODES.PROFESSIONAL_FIRST,
    professionalServices: mode === FLOW_MODES.PROFESSIONAL_FIRST
  };
}

/**
 * Mirrors the backend's save-time guard (it answers 400 otherwise).
 * @param {import('./types').BookingFlowFlags} flags
 * @returns {string|null} what's wrong, or null when valid
 */
export function validateFlags(flags) {
  const modes = [flags.serviceOnly, flags.professionalFirst, flags.profAndSerNoAssociation];
  if (modes.filter(Boolean).length !== 1) {
    return 'Choose exactly one booking flow: service only, professional first, or service + professional.';
  }
  if (flags.areProfessionalsEnabled === flags.serviceOnly) {
    return 'Professionals must be enabled for the professional flows, and off for service only.';
  }
  return null;
}
